// The render loop.
import * as THREE from 'three';
import { PI } from '../lib/math.js';
import { renderer } from './renderer.js';
import { cam, controls, scene, sun } from './scene.js';
import { composer } from './post.js';
import { cranes } from '../world/sagrada.js';
import { treeTime } from '../world/trees.js';
import { updateTrafficLights } from '../world/street-furniture.js';
import { animatePark } from '../world/park.js';
import { walkers } from '../people/models.js';
import { sups } from '../people/crowd.js';
import { walk } from '../people/walking.js';
import { S, overviewFly, wrapAngle } from '../game/ride.js';
import { groundHeight } from '../game/physics.js';
import { chaseCam, placeMoto, ride } from '../game/riding.js';
import { cars } from '../vehicles/cars.js';
import { driveCar } from '../vehicles/traffic.js';
import { clouds } from '../world/clouds.js';
import { animateSea } from '../world/sea-life.js';
import { introCam, nameGroup } from '../game/intro.js';
import { STOPS } from '../data/stops.js';
import { openStop } from '../game/stop-panel.js';
import { finaleCam, updateFireworks } from '../game/finale.js';
import { updateStops } from '../game/stops.js';
import { updateMinimap } from '../ui/minimap.js';
import { fadeU } from './optimise.js';
import { bobbers, idlers, wading } from '../people/characters/loader.js';
import { settleStatics } from '../game/settle.js';
import { flyCam } from '../ui/splash.js';
import { perf, perfTick } from './quality.js';
import { state } from './state.js';

export function startLoop() {
  const clock = new THREE.Clock();
  // shadows refresh at half rate: 1300 casters cost ~9 ms per update, lag of one frame on moving cars is invisible
  renderer.shadowMap.autoUpdate = false;
  let frameN = 0;
  // inspection hook for development only: open the page with ?debug
  if (new URLSearchParams(location.search).has('debug'))
    window.dbg = {
      cam,
      controls,
      S,
      renderer,
      scene,
      cars,
      walkers,
      composer,
      sun,
      setMode: (m) => {
        state.mode = m;
      },
      STOPS,
      openStop,
      groundHeight,
    };
  renderer.setAnimationLoop(() => {
    const dt = Math.min(0.05, clock.getDelta());
    for (const c of cranes) c.top.rotation.y += c.speed * dt;
    if (state.mode === 'ride') {
      if (state.panelOpen) S.v *= Math.exp(-6 * dt);
      else ride(dt);
      if (state.panelOpen) {
        S.x += Math.sin(S.th) * S.v * dt;
        S.z += Math.cos(S.th) * S.v * dt;
      }
      chaseCam(dt);
    } else if (state.mode === 'flyin') flyCam(dt);
    else if (state.mode === 'intro') introCam(treeTime.value);
    else if (state.mode === 'finale') finaleCam(treeTime.value);
    else {
      if (state.mode === 'overview') overviewFly(dt);
      controls.update();
    }
    updateFireworks(dt);
    // portrait phones see much less sideways, so they ride with a wider lens
    const rideFov = cam.aspect < 1 ? 50 : 32;
    if (state.mode === 'ride' && Math.abs(cam.fov - rideFov) > 0.05) {
      cam.fov += (rideFov - cam.fov) * Math.min(1, 2 * dt);
      cam.updateProjectionMatrix();
    }
    updateStops(treeTime.value);
    updateMinimap();
    placeMoto(dt);
    for (const c of cars) if (!c.off) driveCar(c, dt);
    clouds.rotation.y += dt * 0.004;
    treeTime.value += dt;
    animateSea(treeTime.value, dt);
    walk(dt, treeTime.value);
    animatePark(treeTime.value);
    updateTrafficLights(treeTime.value);
    for (const m of idlers) m.update(dt);
    for (const b of bobbers) {
      b.g.position.y = b.y + Math.sin(treeTime.value * 1.4 + b.ph) * 0.035;
      if (b.rad) {
        b.ang += b.w * dt;
        b.g.position.x = b.cx + Math.cos(b.ang) * b.rad;
        b.g.position.z = b.cz + Math.sin(b.ang) * b.rad;
        const s = Math.sign(b.w);
        b.g.rotation.y = Math.atan2(-Math.sin(b.ang) * s, Math.cos(b.ang) * s);
      }
    }
    if (state.rally) state.updateRally(dt);
    for (const b of sups) {
      b.ang += b.w * dt;
      const s = Math.sign(b.w);
      b.g.position.set(
        b.cx + Math.cos(b.ang) * b.rad,
        -0.3 + Math.sin(treeTime.value * 1.3 + b.ph) * 0.04,
        b.cz + Math.sin(b.ang) * b.rad,
      );
      b.g.rotation.y = Math.atan2(-Math.sin(b.ang) * s, Math.cos(b.ang) * s);
      b.g.rotation.z = Math.sin(treeTime.value * 1.1 + b.ph) * 0.03;
      // paddle: sweep from front to back during each stroke, on the side the rider is reaching to
      const a = b.sp.action,
        f = a ? (a.time / 1.6) * PI * 2 : treeTime.value * 4,
        side = Math.sign(Math.sin(f / 2 + 0.001)) || 1;
      // the hands hold the shaft just in front of the chest; it leans out to the stroking side
      b.pivot.position.x = 0.12 * side;
      b.pivot.rotation.z = 0.22 * side;
      b.pivot.rotation.x = -0.55 * Math.cos(f);
      // hands on the shaft: the hand on the stroking side grips low, the other holds the top (the model's L arm is +x)
      if (state.armIK && b.sp.arms) {
        b.g.updateMatrixWorld(true);
        const top = b.pivot.localToWorld(new THREE.Vector3(0, 0.28, 0)),
          low = b.pivot.localToWorld(new THREE.Vector3(0, -0.22, 0));
        const [lowS, topS] = side > 0 ? ['L', 'R'] : ['R', 'L'];
        for (const [bone, q] of b.sp.armRest) bone.quaternion.copy(q);
        for (const [s, T] of [
          [lowS, low],
          [topS, top],
        ]) {
          const sg = s === 'L' ? 1 : -1;
          state.armIK(
            ...b.sp.arms[s],
            T,
            new THREE.Vector3(sg, -1, -0.6).applyQuaternion(b.g.quaternion),
          );
        }
        for (const [bone] of b.sp.armRest) bone.quaternion.normalize();
      }
    }
    for (const w of wading) {
      // waders stroll slowly back and forth through the shallows
      const p = w.holder.position;
      p.x += w.dir * w.v * dt;
      if ((w.dir > 0 && p.x > w.x1) || (w.dir < 0 && p.x < w.x0)) w.dir *= -1;
      w.holder.rotation.y +=
        wrapAngle((w.dir > 0 ? PI / 2 : -PI / 2) - w.holder.rotation.y) * Math.min(1, 3 * dt);
    }
    renderer.shadowMap.needsUpdate = frameN++ % perf.shadowEvery === 0;
    perfTick();
    if (frameN === 90 || frameN === 180) settleStatics(frameN === 180);
    // the floating 3D name is for the opening shot only; while playing it would hang in the sky
    nameGroup.visible = state.mode === 'intro';
    fadeU.uFadeOn.value +=
      ((state.mode === 'ride' ? 1 : 0) - fadeU.uFadeOn.value) * Math.min(1, 3 * dt);
    cam.updateMatrixWorld();
    fadeU.uFadeTo.value.set(S.x, S.y + 1, S.z).applyMatrix4(cam.matrixWorldInverse);
    composer.render();
  });
}
